import React, { useMemo, useState } from "react";
import {
    fmtTime, fmtLong, fmtShort, todayKey, addDays, parseKey, toMin,
    getSlots, overlaps, slotCfg,
} from "../../bookingService";
import BookingEditModal from "./BookingEditModal";

const HOUR_PX = 56;

const PALETTE = [
    ["#dbeafe", "#2563eb"], ["#dcfce7", "#16a34a"], ["#fef3c7", "#d97706"],
    ["#fae8ff", "#c026d3"], ["#fee2e2", "#dc2626"], ["#e0e7ff", "#4f46e5"],
    ["#ccfbf1", "#0d9488"], ["#ffedd5", "#ea580c"], ["#f3e8ff", "#7c3aed"],
    ["#ecfccb", "#65a30d"], ["#fce7f3", "#db2777"], ["#cffafe", "#0891b2"],
];

function resColor(id) {
    let h = 0;
    const s = String(id || "?");
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
    const p = PALETTE[h % PALETTE.length];
    return { bg: p[0], fg: p[1] };
}

function mondayOf(key) {
    const d = parseKey(key);
    return addDays(key, -((d.getDay() + 6) % 7));
}

function monthCells(cursor) {
    const d = parseKey(cursor);
    const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    const lead = (parseKey(first).getDay() + 6) % 7;
    const start = addDays(first, -lead);
    const out = [];
    for (let i = 0; i < 42; i++) out.push(addDays(start, i));
    return out;
}

function hourLabel(h) {
    const ap = h >= 12 ? "PM" : "AM";
    const hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh}:00 ${ap}`;
}

/**
 * Visual calendar for the shop: month / week / day views per resource.
 * Click a booking to manage it, click empty time to book it.
 */
export default function CalendarTab({ shop, resourceTypes, resources, bookings, onStatus, onEdit, onReschedule, onBook }) {
    const [view, setView] = useState("day");
    const [cursor, setCursor] = useState(todayKey());
    const [resFilter, setResFilter] = useState(null); // null = all
    const [weekResId, setWeekResId] = useState("");
    const [detailId, setDetailId] = useState(null);
    const [quick, setQuick] = useState(null); // { resourceId, date, start, end }
    const [editing, setEditing] = useState(null);
    const [moveDate, setMoveDate] = useState("");
    const [moveSlot, setMoveSlot] = useState(null);

    const myResources = useMemo(
        () => resources.filter((r) => r.ownerId === shop.id),
        [resources, shop.id]
    );
    const shown = useMemo(
        () => (!resFilter ? myResources : myResources.filter((r) => resFilter.has(r.id))),
        [myResources, resFilter]
    );
    const resName = (id) => resources.find((r) => r.id === id)?.name || "(deleted)";

    const mine = useMemo(
        () => bookings.filter((b) => b.ownerId === shop.id),
        [bookings, shop.id]
    );
    const byDate = useMemo(() => {
        const map = {};
        for (const b of mine) {
            if (resFilter && !resFilter.has(b.resourceId)) continue;
            (map[b.date] = map[b.date] || []).push(b);
        }
        for (const k of Object.keys(map)) {
            map[k].sort((a, b) => a.start.localeCompare(b.start));
        }
        return map;
    }, [mine, resFilter]);

    const hours = useMemo(() => {
        const types = resourceTypes.filter((t) => t.ownerId === shop.id);
        let lo = 8, hi = 18;
        if (types.length) {
            lo = Math.min(...types.map((t) => slotCfg(t).dayStart));
            hi = Math.max(...types.map((t) => slotCfg(t).dayEnd));
        }
        const out = [];
        for (let h = lo; h < hi; h++) out.push(h);
        return out;
    }, [resourceTypes, shop.id]);

    const label = useMemo(() => {
        if (view === "day") return fmtLong(cursor);
        if (view === "week") {
            const days = [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(mondayOf(cursor), i));
            return `${fmtShort(days[0])} – ${fmtLong(days[6])}`;
        }
        return parseKey(cursor).toLocaleDateString("en-US", { month: "long", year: "numeric" });
    }, [view, cursor]);

    const step = (n) => {
        if (view === "day") setCursor(addDays(cursor, n));
        else if (view === "week") setCursor(addDays(cursor, n * 7));
        else {
            const d = parseKey(cursor);
            d.setMonth(d.getMonth() + n);
            setCursor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`);
        }
    };

    const toggleRes = (id) => {
        setResFilter((prev) => {
            const next = new Set(prev || myResources.map((r) => r.id));
            if (id === "all") return null;
            if (next.has(id)) next.delete(id);
            else next.add(id);
            if (next.size === 0 || next.size === myResources.length) return null;
            return next;
        });
    };

    const openQuick = (resourceId, date, start) => {
        const type = resourceTypes.find((t) => t.id === (resources.find((r) => r.id === resourceId) || {}).typeId);
        const dur = slotCfg(type).slotMin;
        const [h, m] = start.split(":").map(Number);
        const endMin = h * 60 + m + dur;
        const end = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
        setQuick({ resourceId, date, start, end, customer: "", email: "", phone: "", notes: "" });
    };

    const weekDays = [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(mondayOf(cursor), i));
    const weekRes = myResources.find((r) => r.id === weekResId && (!resFilter || resFilter.has(r.id)))
        || myResources.find((r) => !resFilter || resFilter.has(r.id))
        || null;

    const lanes = view === "week"
        ? (weekRes ? weekDays.map((key) => {
            const d = parseKey(key);
            return {
                key: weekRes.id + key, resourceId: weekRes.id, date: key,
                title: d.toLocaleDateString("en-US", { weekday: "short" }),
                sub: fmtShort(key),
            };
        }) : [])
        : shown.map((r) => ({ key: r.id, resourceId: r.id, date: cursor, title: r.name, sub: null }));

    const onPickDetail = (b) => { setDetailId(b.id); setMoveDate(b.date); setMoveSlot(null); };

    const detail = detailId ? mine.find((b) => b.id === detailId) : null;

    const moveType = detail ? resourceTypes.find((t) => t.id === detail.typeId) : null;
    const moveSlots = detail
        ? getSlots(bookings, detail.resourceId, moveDate || detail.date, slotCfg(moveType))
        : [];

    return (
        <div className="space-y-3">
            {/* toolbar */}
            <div className="hk-card">
                <div className="flex flex-wrap justify-between items-center gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => setCursor(todayKey())}>Today</button>
                        <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => step(-1)} aria-label="Previous">←</button>
                        <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => step(1)} aria-label="Next">→</button>
                        <strong className="text-sm ml-1">{label}</strong>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {view === "week" && myResources.length > 1 && (
                            <select
                                className="hk-select"
                                style={{ width: "auto" }}
                                value={weekRes ? weekRes.id : ""}
                                onChange={(e) => setWeekResId(e.target.value)}
                                title="Resource shown in week view"
                            >
                                {myResources.map((r) => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                        )}
                        <div className="flex rounded-lg border border-[#e2e8f0] p-0.5 gap-0.5">
                            {["day", "week", "month"].map((v) => (
                                <button
                                    key={v}
                                    onClick={() => setView(v)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition ${view === v ? "bg-[#0f172a] text-white" : "text-[#64748b] hover:text-[#0f172a]"}`}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {myResources.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-3">
                        <span className="text-[11px] font-bold text-[#64748b]">Resources:</span>
                        <button
                            onClick={() => toggleRes("all")}
                            className={`text-[11px] px-2.5 py-1 rounded-full border transition ${!resFilter ? "border-[#0f172a] font-bold" : "border-[#e2e8f0] text-[#64748b] opacity-70"}`}
                        >
                            All
                        </button>
                        {myResources.map((r) => {
                            const c = resColor(r.id);
                            const on = !resFilter || resFilter.has(r.id);
                            return (
                                <button
                                    key={r.id}
                                    onClick={() => toggleRes(r.id)}
                                    className={`text-[11px] px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${on ? "border-[#0f172a] font-bold" : "border-[#e2e8f0] text-[#64748b] opacity-60"}`}
                                >
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.fg }} />
                                    {r.name}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {myResources.length === 0 ? (
                <div className="hk-card">
                    <p className="text-sm text-[#64748b] mb-0">No resources yet — add doctors or rooms in the Calendars tab first.</p>
                </div>
            ) : view === "month" ? (
                <MonthView cells={monthCells(cursor)} byDate={byDate} cursor={cursor}
                    onPick={onPickDetail}
                    onDay={(key) => { setCursor(key); setView("day"); }} />
            ) : (
                <TimeGridView lanes={lanes} hours={hours} byDate={byDate}
                    onPick={onPickDetail}
                    onQuick={openQuick} />
            )}

            {detail && (
                <DetailModal
                    b={detail} resName={resName(detail.resourceId)}
                    onClose={() => { setDetailId(null); setEditing(null); }}
                    onStatus={(s) => onStatus(detail.id, s)}
                    onEdit={() => setEditing(detail)}
                    onCancel={() => {
                        if (window.confirm(`Cancel booking ${detail.id}?`)) {
                            onStatus(detail.id, "cancelled");
                        }
                    }}
                    moveDate={moveDate} setMoveDate={(d) => { setMoveDate(d); setMoveSlot(null); }}
                    moveSlots={moveSlots} moveSlot={moveSlot} setMoveSlot={setMoveSlot}
                    onMove={() => {
                        onReschedule(detail.id, { date: moveDate, start: moveSlot.start, end: moveSlot.end });
                        setMoveSlot(null);
                    }}
                />
            )}

            {editing && (
                <BookingEditModal
                    booking={editing}
                    resources={resources}
                    bookings={bookings}
                    onClose={() => setEditing(null)}
                    onSave={(patch) => {
                        onEdit(editing.id, patch);
                        setEditing(null);
                    }}
                />
            )}

            {quick && (
                <QuickBookModal
                    quick={quick} setQuick={setQuick}
                    resName={resName(quick.resourceId)}
                    onConfirm={() => {
                        if (!quick.customer.trim()) return "Patient name is required.";
                        if (overlaps(bookings, quick.resourceId, quick.date, quick.start, quick.end, null)) {
                            return "That slot was just taken.";
                        }
                        const r = resources.find((x) => x.id === quick.resourceId) || {};
                        onBook({
                            shopId: shop.id, typeId: r.typeId, resourceId: quick.resourceId,
                            date: quick.date, start: quick.start, end: quick.end,
                            customer: quick.customer.trim(), email: quick.email.trim(),
                            phone: quick.phone.trim(), notes: quick.notes.trim(),
                        });
                        setQuick(null);
                        return null;
                    }}
                />
            )}
        </div>
    );
}

/* ---------- month ---------- */
function MonthView({ cells, byDate, cursor, onPick, onDay }) {
    const month = parseKey(cursor).getMonth();
    const tk = todayKey();
    return (
        <div className="hk-table-wrap">
            <div className="min-w-[700px]">
                <div className="grid grid-cols-7">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                        <div key={d} className="px-2 py-2.5 text-[11px] font-bold text-[#64748b] text-center border-b border-[#e2e8f0]">{d}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7">
                    {cells.map((key) => {
                        const d = parseKey(key);
                        const inMonth = d.getMonth() === month;
                        const list = byDate[key] || [];
                        return (
                            <div
                                key={key}
                                onClick={() => onDay(key)}
                                className={`min-h-[104px] p-1.5 border-b border-r border-[#f1f5f9] cursor-pointer hover:bg-[#f8fafc] ${inMonth ? "" : "bg-[#f8fafc] text-[#94a3b8]"} ${key === tk ? "bg-[#eff6ff]" : ""}`}
                            >
                                <div className="text-xs font-bold mb-1">{d.getDate()}</div>
                                {list.slice(0, 3).map((b) => {
                                    const c = resColor(b.resourceId);
                                    return (
                                        <button
                                            key={b.id}
                                            onClick={(e) => { e.stopPropagation(); onPick(b); }}
                                            className="block w-full text-left text-[10px] border border-[#e2e8f0] bg-white rounded px-1.5 py-0.5 mb-0.5 truncate hover:border-[#0f172a]"
                                            style={{ borderLeft: `3px solid ${c.fg}` }}
                                        >
                                            {b.start.slice(0, 5)} {b.customer}
                                        </button>
                                    );
                                })}
                                {list.length > 3 && (
                                    <div className="text-[10px] text-[#64748b]">+{list.length - 3} more</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/* ---------- time grid ----------
   Day: one column per resource. Week: one column per day for one resource. */
function TimeGridView({ lanes, hours, byDate, onPick, onQuick }) {
    const tk = todayKey();
    const now = new Date();
    const nowM = now.getHours() * 60 + now.getMinutes();
    const dayStart = hours.length ? hours[0] : 8;
    const dayEnd = hours.length ? hours[hours.length - 1] + 1 : 18;
    const cols = lanes.length || 1;
    const showNow = lanes.some((l) => l.date === tk) && nowM >= dayStart * 60 && nowM <= dayEnd * 60;

    const styleFor = (b) => {
        const top = ((toMin(b.start) - dayStart * 60) / 60) * HOUR_PX;
        let h = ((toMin(b.end) - toMin(b.start)) / 60) * HOUR_PX - 6;
        if (h < 44) h = 44;
        return { top: Math.max(top, 0), height: h };
    };

    if (!lanes.length) {
        return (
            <div className="hk-card">
                <p className="text-sm text-[#64748b] mb-0">No resources to show — adjust the filter or add resources in Calendars.</p>
            </div>
        );
    }

    return (
        <div className="hk-table-wrap">
            <div style={{ minWidth: Math.max(560, cols * 150) }}>
                <div className="grid bg-white border-b border-[#e2e8f0] sticky top-0" style={{ gridTemplateColumns: `72px repeat(${cols}, minmax(140px, 1fr))` }}>
                    <div className="border-r border-[#e2e8f0]" />
                    {lanes.map((lane) => (
                        <div key={lane.key} className="px-3 py-2.5 border-r border-[#e2e8f0] last:border-r-0">
                            <div className="text-[13px] font-semibold truncate">{lane.title}</div>
                            {lane.sub && <div className="text-[11px] text-[#64748b] truncate">{lane.sub}</div>}
                        </div>
                    ))}
                </div>
                <div className="grid relative" style={{ gridTemplateColumns: `72px repeat(${cols}, minmax(140px, 1fr))` }}>
                    <div>
                        {hours.map((h) => (
                            <div key={h} className="px-2 py-2 border-r border-[#e2e8f0] border-b border-[#f1f5f9] text-[11px] text-[#64748b] bg-white" style={{ height: HOUR_PX }}>
                                {hourLabel(h)}
                            </div>
                        ))}
                    </div>
                    {lanes.map((lane) => (
                        <div key={lane.key} className="relative border-r border-[#e2e8f0] last:border-r-0">
                            {hours.map((h) => (
                                <div
                                    key={h}
                                    onClick={() => onQuick(lane.resourceId, lane.date, `${String(h).padStart(2, "0")}:00`)}
                                    title={`Book ${lane.title} at ${hourLabel(h)}`}
                                    className="border-b border-[#f1f5f9] cursor-crosshair hover:bg-[#eff6ff]"
                                    style={{ height: HOUR_PX }}
                                />
                            ))}
                            {(byDate[lane.date] || []).filter((b) => b.resourceId === lane.resourceId).map((b) => {
                                const c = resColor(b.resourceId);
                                const s = styleFor(b);
                                const past = lane.date < tk || (lane.date === tk && toMin(b.end) <= nowM);
                                return (
                                    <button
                                        key={b.id}
                                        onClick={() => onPick(b)}
                                        className="absolute left-[5px] right-[5px] text-left border rounded-lg bg-white px-2 py-1.5 overflow-hidden hover:shadow-md hover:z-10"
                                        style={{ top: s.top, height: s.height, borderColor: "#cbd5e1", borderLeft: `3px solid ${c.fg}`, opacity: b.status === "cancelled" || past ? 0.6 : 1 }}
                                    >
                                        <div className="text-xs font-semibold truncate">{b.customer}</div>
                                        <div className="text-[10px] text-[#64748b]">{b.start.slice(0, 5)}–{b.end.slice(0, 5)}</div>
                                        <span className={`hk-pill ${b.status === "confirmed" ? "hk-pill-green" : b.status === "pending" ? "hk-pill-amber" : b.status === "cancelled" ? "hk-pill-red" : b.status === "completed" ? "hk-pill-blue" : "hk-pill-zinc"}`}>
                                            {b.status}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                    {showNow && (
                        <div className="absolute left-[72px] right-0 h-0.5 bg-red-600 pointer-events-none" style={{ top: ((nowM - dayStart * 60) / 60) * HOUR_PX }}>
                            <span className="absolute -left-16 -top-2 bg-red-600 text-white text-[9px] font-bold rounded px-1 py-0.5 whitespace-nowrap">
                                {fmtTime(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------- detail modal ---------- */
function DetailModal({ b, resName, onClose, onStatus, onEdit, onCancel, moveDate, setMoveDate, moveSlots, moveSlot, setMoveSlot, onMove }) {
    return (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(15,23,42,.45)" }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content" style={{ borderRadius: 14, border: "none" }}>
                    <div className="modal-header">
                        <div>
                            <div className="hk-mono text-[11px] text-[#64748b]">{b.id}</div>
                            <h5 className="modal-title font-bold">{b.customer}</h5>
                        </div>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>
                    <div className="modal-body">
                        <label className="hk-label">Status</label>
                        <select className="hk-select mb-3" style={{ textTransform: "capitalize" }} value={b.status} onChange={(e) => onStatus(e.target.value)}>
                            {["pending", "confirmed", "completed", "cancelled", "noshow"].map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                        <div className="hk-card !p-3 mb-3">
                            <InfoRow k="Service" v={b.type} />
                            <InfoRow k="Resource" v={resName} />
                            <InfoRow k="When" v={`${b.date} · ${fmtTime(b.start)} – ${fmtTime(b.end)}`} />
                            {b.email && <InfoRow k="Email" v={b.email} />}
                            {b.phone && <InfoRow k="Phone" v={b.phone} />}
                            {b.notes && <InfoRow k="Notes" v={b.notes} />}
                        </div>
                        {(b.status === "confirmed" || b.status === "pending") && (
                            <>
                                <label className="hk-label">Move to another time</label>
                                <input type="date" className="hk-input mb-2" value={moveDate} min={todayKey()}
                                    onChange={(e) => { setMoveDate(e.target.value); setMoveSlot(null); }} />
                                <div className="flex flex-wrap gap-1.5 mb-1">
                                    {moveSlots.map((s) => (
                                        <button
                                            key={s.start}
                                            disabled={!s.available}
                                            onClick={() => setMoveSlot(s)}
                                            className={`hk-btn hk-btn-sm ${moveSlot?.start === s.start ? "hk-btn-primary" : "hk-btn-outline"}`}
                                            style={!s.available && moveSlot?.start !== s.start ? { opacity: 0.4 } : undefined}
                                        >
                                            {fmtTime(s.start)}
                                        </button>
                                    ))}
                                </div>
                                <button className="hk-btn hk-btn-outline hk-btn-sm mt-2" disabled={!moveSlot} onClick={onMove}>
                                    Confirm new time
                                </button>
                            </>
                        )}
                    </div>
                    <div className="modal-footer flex-wrap">
                        <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={onClose}>Close</button>
                        <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={onEdit}>Edit details</button>
                        {b.status !== "cancelled" && (
                            <button className="hk-btn hk-btn-outline hk-btn-sm" style={{ color: "var(--hk-danger)" }} onClick={onCancel}>
                                Cancel booking
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function InfoRow({ k, v }) {
    return (
        <div className="flex justify-between gap-4 py-1 text-xs">
            <span className="text-[#64748b] shrink-0">{k}</span>
            <span className="text-right break-words">{v}</span>
        </div>
    );
}

/* ---------- quick-book modal ---------- */
function QuickBookModal({ quick, setQuick, resName, onConfirm }) {
    const [error, setError] = useState("");
    const set = (k, v) => setQuick((q) => ({ ...q, [k]: v }));

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(15,23,42,.45)" }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content" style={{ borderRadius: 14, border: "none" }}>
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title font-bold">New booking</h5>
                            <div className="text-xs text-[#64748b]">{resName} · {quick.date} · {fmtTime(quick.start)} – {fmtTime(quick.end)}</div>
                        </div>
                        <button type="button" className="btn-close" onClick={() => setQuick(null)} aria-label="Close" />
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger py-2">{error}</div>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                                <label className="hk-label">Patient name *</label>
                                <input className="hk-input" value={quick.customer} onChange={(e) => set("customer", e.target.value)} />
                            </div>
                            <div>
                                <label className="hk-label">Service / reason</label>
                                <input className="hk-input" value={quick.type || ""} onChange={(e) => set("type", e.target.value)} placeholder="Consultation" />
                            </div>
                            <div>
                                <label className="hk-label">Email</label>
                                <input className="hk-input" value={quick.email} onChange={(e) => set("email", e.target.value)} />
                            </div>
                            <div>
                                <label className="hk-label">Phone</label>
                                <input className="hk-input" value={quick.phone} onChange={(e) => set("phone", e.target.value)} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="hk-label">Notes</label>
                                <input className="hk-input" value={quick.notes} onChange={(e) => set("notes", e.target.value)} />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => setQuick(null)}>Cancel</button>
                        <button
                            className="hk-btn hk-btn-primary hk-btn-sm"
                            onClick={() => {
                                const err = onConfirm();
                                if (err) setError(err);
                            }}
                        >
                            Create booking
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
